/**
 * Label layout, and the editorial annotation composited over the finished
 * photograph.
 *
 * The print finish used to live here too — a radial vignette and a noise tile —
 * duplicating what the CSS did over the canvas, at a different relative scale, and
 * in the encoded domain where neither behaves like light. Both are the
 * compositor's now, in linear light, once, identically on screen and in the file.
 * What is left is type: title, city names, credits.
 */

import {
  BACKGROUND,
  LABEL,
  LEADER,
  MUTED,
  SANS_FONT,
  TEXT,
  TITLE_FONT,
} from "../../shared/constants.ts";

export interface ProjectedCity {
  name: string;
  anchorX: number;
  anchorY: number;
  population: number;
  visible: boolean;
}

export interface PosterLabel extends ProjectedCity {
  x: number;
  y: number;
}

export interface PosterOverlay {
  countryName: string;
  title: boolean;
  year: number;
  hint: string;
}

interface Rectangle {
  left: number;
  top: number;
  right: number;
  bottom: number;
}

const clamp = (value: number, low: number, high: number): number =>
  value < low ? low : value > high ? high : value;

export function posterLabelSize(height: number): number {
  return clamp(height * 0.0106, 9, 17);
}

/**
 * Place larger cities first, trying six restrained offsets around the anchor.
 * A label that cannot fit without colliding is omitted rather than piled onto a
 * more important name.
 */
export function layoutCityLabels(
  cities: ReadonlyArray<ProjectedCity>,
  width: number,
  height: number,
  titleVisible: boolean,
  measure: CanvasRenderingContext2D,
): PosterLabel[] {
  const fontSize = posterLabelSize(height);
  const lineHeight = fontSize * 1.45;
  const spacing = fontSize * 0.04;
  measure.font = `500 ${fontSize}px ${SANS_FONT}`;

  const result: PosterLabel[] = cities.map((city) => ({ ...city, x: 0, y: 0, visible: false }));
  const occupied: Rectangle[] = [];
  if (titleVisible) {
    occupied.push({ left: width * 0.22, top: height * 0.02, right: width * 0.78, bottom: height * 0.16 });
  }
  occupied.push({ left: 0, top: height * 0.945, right: width, bottom: height });

  const order = cities
    .map((city, index) => ({ city, index }))
    .sort((first, second) => second.city.population - first.city.population);

  for (const { city, index } of order) {
    if (!city.visible) continue;
    const text = city.name.toLocaleUpperCase();
    const textWidth = measure.measureText(text).width + Math.max(text.length - 1, 0) * spacing;
    const labelWidth = textWidth + fontSize * 0.72;
    const labelHeight = lineHeight;
    const horizontal = labelWidth * 0.52 + fontSize * 0.45;
    const vertical = labelHeight * 0.9;
    const candidates: ReadonlyArray<readonly [number, number]> = [
      [0, -vertical],
      [horizontal, -labelHeight * 0.42],
      [-horizontal, -labelHeight * 0.42],
      [horizontal, labelHeight * 0.55],
      [-horizontal, labelHeight * 0.55],
      [0, vertical],
    ];

    for (const [offsetX, offsetY] of candidates) {
      const x = city.anchorX + offsetX;
      const y = city.anchorY + offsetY;
      const rectangle: Rectangle = {
        left: x - labelWidth * 0.5,
        top: y - labelHeight * 0.5,
        right: x + labelWidth * 0.5,
        bottom: y + labelHeight * 0.5,
      };
      if (
        rectangle.left < 8 ||
        rectangle.right > width - 8 ||
        rectangle.top < 8 ||
        rectangle.bottom > height - 8 ||
        occupied.some((other) => overlaps(rectangle, other, 4))
      ) {
        continue;
      }
      occupied.push(rectangle);
      result[index] = { ...city, x, y, visible: true };
      break;
    }
  }
  return result;
}

function overlaps(first: Rectangle, second: Rectangle, gap: number): boolean {
  return !(
    first.right + gap <= second.left ||
    first.left >= second.right + gap ||
    first.bottom + gap <= second.top ||
    first.top >= second.bottom + gap
  );
}

/** Draw title, collision-free labels and credits over a rendered frame. */
export function paintPoster(
  context: CanvasRenderingContext2D,
  width: number,
  height: number,
  overlay: PosterOverlay,
  labels: ReadonlyArray<PosterLabel>,
): void {
  // Render-target readback may carry translucent edge pixels. Flattening onto
  // paper makes the PNG deterministic and prevents black halos in viewers that
  // composite transparent RGB against a dark background.
  context.save();
  context.globalCompositeOperation = "destination-over";
  context.fillStyle = BACKGROUND;
  context.fillRect(0, 0, width, height);
  context.restore();

  if (overlay.title) paintTitle(context, width, height, overlay.countryName);
  paintLabels(context, height, labels);
  paintFooter(context, width, height, overlay);
}

function paintTitle(
  context: CanvasRenderingContext2D,
  width: number,
  height: number,
  countryName: string,
): void {
  const kickerSize = clamp(height * 0.012, 9, 20);
  const titleSize = clamp(height * 0.066, 30, 104);
  const top = height * 0.046;
  context.save();
  context.textAlign = "center";
  context.textBaseline = "top";
  context.fillStyle = MUTED;
  context.font = `400 ${kickerSize}px ${SANS_FONT}`;
  drawSpacedText(context, "POPULATION DENSITY MAP", width * 0.5, top, kickerSize * 0.06);
  context.fillStyle = TEXT;
  context.font = `400 ${titleSize}px ${TITLE_FONT}`;
  context.fillText(countryName.toLocaleUpperCase(), width * 0.5, top + kickerSize * 1.55);
  context.restore();
}

function paintLabels(
  context: CanvasRenderingContext2D,
  height: number,
  labels: ReadonlyArray<PosterLabel>,
): void {
  const fontSize = posterLabelSize(height);
  const spacing = fontSize * 0.04;
  context.save();
  context.font = `500 ${fontSize}px ${SANS_FONT}`;
  context.textAlign = "center";
  context.textBaseline = "middle";
  context.lineWidth = Math.max(1, height / 1600);

  for (const label of labels) {
    if (!label.visible) continue;
    context.strokeStyle = LEADER;
    context.globalAlpha = 0.68;
    context.beginPath();
    context.moveTo(label.anchorX, label.anchorY);
    context.lineTo(label.x, label.y);
    context.stroke();
    context.fillStyle = BACKGROUND;
    context.strokeStyle = LEADER;
    context.globalAlpha = 1;
    context.beginPath();
    context.arc(label.anchorX, label.anchorY, Math.max(1.8, fontSize * 0.16), 0, Math.PI * 2);
    context.fill();
    context.stroke();
  }

  for (const label of labels) {
    if (!label.visible) continue;
    const text = label.name.toLocaleUpperCase();
    const textWidth = context.measureText(text).width + Math.max(text.length - 1, 0) * spacing;
    const paddingX = fontSize * 0.36;
    const boxHeight = fontSize * 1.45;
    context.globalAlpha = 0.88;
    context.fillStyle = BACKGROUND;
    roundedRectangle(
      context,
      label.x - textWidth * 0.5 - paddingX,
      label.y - boxHeight * 0.5,
      textWidth + paddingX * 2,
      boxHeight,
      fontSize * 0.18,
    );
    context.fill();
    context.globalAlpha = 1;
    context.fillStyle = LABEL;
    drawSpacedText(context, text, label.x, label.y, spacing, true);
  }
  context.restore();
}

function paintFooter(
  context: CanvasRenderingContext2D,
  width: number,
  height: number,
  overlay: PosterOverlay,
): void {
  const size = clamp(height * 0.0084, 8, 14);
  const lineHeight = size * 2.1;
  const bottom = height * 0.021;
  context.save();
  context.fillStyle = MUTED;
  context.font = `400 ${size}px ${SANS_FONT}`;
  context.textBaseline = "alphabetic";
  context.textAlign = "left";
  context.fillText("VISUALISED WITH POP3D", width * 0.035, height - bottom - lineHeight);
  context.fillText(`WORLDPOP ${overlay.year} · GEOBOUNDARIES`, width * 0.035, height - bottom);
  context.textAlign = "right";
  context.fillText(overlay.hint.toLocaleUpperCase(), width * 0.965, height - bottom);
  context.restore();
}

function drawSpacedText(
  context: CanvasRenderingContext2D,
  text: string,
  x: number,
  y: number,
  spacing: number,
  middle = false,
): void {
  if (spacing === 0 || text.length < 2) {
    context.fillText(text, x, y);
    return;
  }
  const widths = Array.from(text, (character) => context.measureText(character).width);
  const total = widths.reduce((sum, value) => sum + value, 0) + spacing * (widths.length - 1);
  let cursor = x - total * 0.5;
  const baseline = middle ? y : y;
  const alignment = context.textAlign;
  context.textAlign = "left";
  for (let index = 0; index < text.length; index += 1) {
    context.fillText(text[index], cursor, baseline);
    cursor += widths[index] + spacing;
  }
  context.textAlign = alignment;
}

function roundedRectangle(
  context: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  height: number,
  radius: number,
): void {
  const corner = Math.min(radius, width * 0.5, height * 0.5);
  context.beginPath();
  context.moveTo(x + corner, y);
  context.lineTo(x + width - corner, y);
  context.quadraticCurveTo(x + width, y, x + width, y + corner);
  context.lineTo(x + width, y + height - corner);
  context.quadraticCurveTo(x + width, y + height, x + width - corner, y + height);
  context.lineTo(x + corner, y + height);
  context.quadraticCurveTo(x, y + height, x, y + height - corner);
  context.lineTo(x, y + corner);
  context.quadraticCurveTo(x, y, x + corner, y);
  context.closePath();
}
